// All 15 module screens — compact but full-featured (list + create/view forms).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, FileText, Send, Ban, ScrollText, Loader2 } from "lucide-react";
import { PageHeader, StatusPill } from "@/components/PageHeader";
import { ListPage, statusCol } from "@/components/ListPage";
import { DataTable } from "@/components/DataTable";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";
import { fmtMoney, fmtDate, fmtDateTime, isoDate } from "@/utils/format";
import { useFxRates } from "@/hooks/useFxRates";
import { DateCell } from "@/components/DateCell";
import { toast } from "sonner";

// Tutar + TL karşılığı (para birimi TRY değilse parantez içinde TL değeri eklenir).
function fmtAmtTL(amount, currency, toTRY) {
  const ccy = String(currency || "TRY").toUpperCase();
  const base = fmtMoney(amount, ccy);
  if (ccy === "TRY" || ccy === "TL") return base;
  const tl = toTRY ? toTRY(amount, ccy) : null;
  return tl ? `${base} (${fmtMoney(tl, "TRY")})` : base;
}

// ---------------- PARTIES ----------------
export function PartiesPage() {
  const nav = useNavigate();
  return <ListPage
    testId="parties-list"
    title="Cari Kartlar" subtitle="Müşteri, tedarikçi ve personel cari kartları"
    endpoint="/parties" createHref="/parties/new"
    onView={(r) => nav(`/parties/${r.id}`)} canDelete
    columns={[
      { key: "code", header: "Kod" },
      { key: "name", header: "Ad", render: (r) => <span className="font-medium">{r.name}</span> },
      { key: "type", header: "Tür", render: (r) => <StatusPill status={r.type} label={r.type} /> },
      { key: "tax_id", header: "VKN" },
      { key: "phone", header: "Telefon" },
      { key: "credit_limit", header: "Kredi Limiti", align: "right", render: (r) => fmtMoney(r.credit_limit) },
    ]}
  />;
}

export function PartyFormPage() {
  const nav = useNavigate(); const { id } = useParams();
  const isNew = id === "new";
  const [form, setForm] = useState({ type: "customer", name: "", tax_id: "", phone: "", email: "", credit_limit: "0", payment_terms_days: 30 });
  const [detail, setDetail] = useState(null);
  const [statement, setStatement] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/parties/${id}`).then(({ data }) => setDetail(data));
    api.get(`/parties/${id}/statement`).then(({ data }) => setStatement(data));
  }, [id, isNew]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/parties", form);
      toast.success("Cari eklendi"); nav("/parties");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!isNew && detail) {
    return (
      <div data-testid="party-detail">
        <PageHeader
          title={`${detail.code} · ${detail.name}`}
          subtitle={`${detail.type} · VKN: ${detail.tax_id || "—"}`}
          actions={<Button variant="outline" onClick={() => nav("/parties")}><ArrowLeft className="h-4 w-4 mr-2" />Geri</Button>}
        />
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="surface p-5"><div className="label-caps">Bakiye</div><div className="text-2xl font-heading font-bold mt-2 tabular">{statement && fmtMoney(statement.balance)}</div></div>
          <div className="surface p-5"><div className="label-caps">Kredi Limiti</div><div className="text-2xl font-heading font-bold mt-2 tabular">{fmtMoney(detail.credit_limit)}</div></div>
          <div className="surface p-5"><div className="label-caps">Vade (Gün)</div><div className="text-2xl font-heading font-bold mt-2 tabular">{detail.payment_terms_days}</div></div>
        </div>
        <div className="surface p-5">
          <h3 className="font-heading font-semibold mb-3">Cari Ekstre</h3>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border">
              <th className="label-caps py-2 text-left">Tarih</th>
              <th className="label-caps py-2 text-left">Yevmiye No</th>
              <th className="label-caps py-2 text-left">Açıklama</th>
              <th className="label-caps py-2 text-right">Borç</th>
              <th className="label-caps py-2 text-right">Alacak</th>
            </tr></thead>
            <tbody>
              {statement?.movements?.length ? statement.movements.map((m, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5 tabular">{fmtDate(m.posted_at)}</td>
                  <td className="py-1.5">{m.journal_no}</td>
                  <td className="py-1.5">{m.description}</td>
                  <td className="py-1.5 text-right tabular">{parseFloat(m.line.debit) > 0 ? fmtMoney(m.line.debit) : "—"}</td>
                  <td className="py-1.5 text-right tabular">{parseFloat(m.line.credit) > 0 ? fmtMoney(m.line.credit) : "—"}</td>
                </tr>
              )) : <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Hareket yok</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="party-form">
      <PageHeader title="Yeni Cari Kart" actions={<Button variant="outline" onClick={() => nav("/parties")}><ArrowLeft className="h-4 w-4 mr-2" />Geri</Button>} />
      <form onSubmit={submit} className="surface p-6 max-w-2xl space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Tür</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="party-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Müşteri</SelectItem>
                <SelectItem value="supplier">Tedarikçi</SelectItem>
                <SelectItem value="both">Her İkisi</SelectItem>
                <SelectItem value="employee">Personel</SelectItem>
                <SelectItem value="other">Diğer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Ad *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="party-name" /></div>
          <div><Label>VKN / TCKN</Label><Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} /></div>
          <div><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Kredi Limiti (₺)</Label><Input type="number" step="0.01" value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} /></div>
          <div><Label>Vade Günü</Label><Input type="number" value={form.payment_terms_days} onChange={e => setForm({ ...form, payment_terms_days: +e.target.value })} /></div>
        </div>
        <div className="flex gap-2 justify-end pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={() => nav("/parties")}>İptal</Button>
          <Button type="submit" disabled={busy} data-testid="party-submit"><Save className="h-4 w-4 mr-2" />Kaydet</Button>
        </div>
      </form>
    </div>
  );
}

// ---------------- INVOICES ----------------
export function InvoicesPage() {
  const nav = useNavigate();
  return <ListPage
    testId="invoices-list"
    title="Faturalar" subtitle="Satış ve alış faturaları"
    endpoint="/invoices" createHref="/invoices/new"
    onView={(r) => nav(`/invoices/${r.id}`)}
    columns={[
      { key: "invoice_no", header: "Fatura No", render: (r) => <span className="font-mono font-medium">{r.invoice_no}</span> },
      { key: "type", header: "Tür", render: (r) => <StatusPill status={r.type} label={r.type === "sales" ? "Satış" : "Alış"} /> },
      { key: "invoice_date", header: "Tarih", render: (r) => fmtDate(r.invoice_date) },
      { key: "due_date", header: "Vade", render: (r) => fmtDate(r.due_date) },
      { key: "grand_total", header: "Tutar", align: "right", render: (r) => fmtMoney(r.grand_total) },
      { key: "paid_amount", header: "Ödenen", align: "right", render: (r) => fmtMoney(r.paid_amount) },
      { key: "status", header: "Durum", render: (r) => <StatusPill status={r.status} /> },
      { key: "payment_status", header: "Ödeme", render: (r) => <StatusPill status={r.payment_status} /> },
    ]}
  />;
}

export function InvoiceFormPage() {
  const nav = useNavigate(); const { id } = useParams();
  const isNew = id === "new";
  const [parties, setParties] = useState([]);
  const [form, setForm] = useState({
    type: "sales", party_id: "", invoice_date: isoDate(), due_date: isoDate(new Date(Date.now() + 30 * 86400000)),
    notes: "", lines: [{ description: "", quantity: "1", unit_price: "0", vat_rate: "20", discount_pct: "0" }]
  });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/parties?limit=500").then(({ data }) => setParties(data)); }, []);
  useEffect(() => { if (!isNew) api.get(`/invoices/${id}`).then(({ data }) => setDetail(data)); }, [id, isNew]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    form.lines.forEach(ln => {
      const q = +ln.quantity || 0, up = +ln.unit_price || 0, vr = +ln.vat_rate || 0, dp = +ln.discount_pct || 0;
      const net = q * up * (1 - dp / 100);
      sub += net; tax += net * vr / 100;
    });
    return { sub, tax, grand: sub + tax };
  }, [form.lines]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/invoices", form);
      toast.success(`Fatura oluşturuldu: ${data.invoice_no}`);
      nav(`/invoices/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const postInvoice = async () => {
    try {
      await api.post(`/invoices/${id}/post`);
      toast.success("Fatura muhasebeleştirildi");
      const { data } = await api.get(`/invoices/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancelInvoice = async () => {
    const reason = window.prompt("İptal gerekçesi:");
    if (!reason) return;
    try {
      await api.post(`/invoices/${id}/cancel`, { reason });
      toast.success("Fatura iptal edildi");
      const { data } = await api.get(`/invoices/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!isNew && detail) {
    return (
      <div data-testid="invoice-detail">
        <PageHeader
          title={`Fatura ${detail.invoice_no}`}
          subtitle={`${detail.type === "sales" ? "Satış" : "Alış"} · ${fmtDate(detail.invoice_date)}`}
          actions={
            <>
              <Button variant="outline" onClick={() => nav("/invoices")}><ArrowLeft className="h-4 w-4 mr-2" />Geri</Button>
              {detail.status === "draft" && <Button onClick={postInvoice} data-testid="invoice-post"><Send className="h-4 w-4 mr-2" />Muhasebeleştir</Button>}
              {detail.status === "posted" && <Button variant="destructive" onClick={cancelInvoice}><Ban className="h-4 w-4 mr-2" />İptal Et</Button>}
            </>
          }
        />
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="surface p-5"><div className="label-caps">Durum</div><div className="mt-2"><StatusPill status={detail.status} /></div></div>
          <div className="surface p-5"><div className="label-caps">Ödeme</div><div className="mt-2"><StatusPill status={detail.payment_status} /></div></div>
          <div className="surface p-5"><div className="label-caps">Ara Toplam</div><div className="text-lg font-bold tabular mt-2">{fmtMoney(detail.subtotal)}</div></div>
          <div className="surface p-5"><div className="label-caps">Genel Toplam</div><div className="text-lg font-bold tabular mt-2">{fmtMoney(detail.grand_total)}</div></div>
        </div>
        <div className="surface p-5 mb-6">
          <h3 className="font-heading font-semibold mb-3">Kalemler</h3>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border">
              <th className="label-caps py-2 text-left">Açıklama</th>
              <th className="label-caps py-2 text-right">Miktar</th>
              <th className="label-caps py-2 text-right">Birim</th>
              <th className="label-caps py-2 text-right">KDV %</th>
              <th className="label-caps py-2 text-right">Net</th>
              <th className="label-caps py-2 text-right">KDV</th>
              <th className="label-caps py-2 text-right">Toplam</th>
            </tr></thead>
            <tbody>{detail.lines?.map(l => (
              <tr key={l.line_no} className="border-b border-border">
                <td className="py-1.5">{l.description}</td>
                <td className="py-1.5 text-right tabular">{l.quantity}</td>
                <td className="py-1.5 text-right tabular">{fmtMoney(l.unit_price)}</td>
                <td className="py-1.5 text-right tabular">{l.vat_rate}</td>
                <td className="py-1.5 text-right tabular">{fmtMoney(l.line_net)}</td>
                <td className="py-1.5 text-right tabular">{fmtMoney(l.line_vat)}</td>
                <td className="py-1.5 text-right tabular font-medium">{fmtMoney(l.line_total)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="surface p-5">
          <h3 className="font-heading font-semibold mb-3">Dosya Ekleri</h3>
          <FileUpload linkedEntity="invoice" linkedId={detail.id} testId="invoice-upload" />
        </div>
      </div>
    );
  }

  const updateLine = (i, k, v) => {
    const lines = [...form.lines]; lines[i] = { ...lines[i], [k]: v }; setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { description: "", quantity: "1", unit_price: "0", vat_rate: "20", discount_pct: "0" }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="invoice-form">
      <PageHeader title="Yeni Fatura" actions={<Button variant="outline" onClick={() => nav("/invoices")}><ArrowLeft className="h-4 w-4 mr-2" />Geri</Button>} />
      <form onSubmit={submit} className="surface p-6 space-y-4">
        <div className="grid md:grid-cols-4 gap-4">
          <div><Label>Tür</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="invoice-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sales">Satış</SelectItem>
                <SelectItem value="purchase">Alış</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Cari *</Label>
            <Select value={form.party_id} onValueChange={v => setForm({ ...form, party_id: v })}>
              <SelectTrigger data-testid="invoice-party"><SelectValue placeholder="Seçin..." /></SelectTrigger>
              <SelectContent>
                {parties.map(p => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Tarih</Label><Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
          <div><Label>Vade</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Fatura Kalemleri</Label>
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line">+ Kalem Ekle</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border">
                <th className="label-caps py-2 text-left w-2/5">Açıklama</th>
                <th className="label-caps py-2 text-right">Miktar</th>
                <th className="label-caps py-2 text-right">Birim Fiyat</th>
                <th className="label-caps py-2 text-right">KDV %</th>
                <th className="label-caps py-2 text-right">İnd %</th>
                <th className="label-caps py-2 text-right">Toplam</th>
                <th />
              </tr></thead>
              <tbody>{form.lines.map((ln, i) => {
                const net = (+ln.quantity || 0) * (+ln.unit_price || 0) * (1 - (+ln.discount_pct || 0) / 100);
                const total = net * (1 + (+ln.vat_rate || 0) / 100);
                return (
                <tr key={i} className="border-b border-border">
                  <td className="py-1"><Input value={ln.description} onChange={e => updateLine(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} /></td>
                  <td className="py-1"><Input type="number" step="0.01" className="text-right tabular w-24" value={ln.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} /></td>
                  <td className="py-1"><Input type="number" step="0.01" className="text-right tabular w-32" value={ln.unit_price} onChange={e => updateLine(i, "unit_price", e.target.value)} /></td>
                  <td className="py-1"><Input type="number" step="0.01" className="text-right tabular w-20" value={ln.vat_rate} onChange={e => updateLine(i, "vat_rate", e.target.value)} /></td>
                  <td className="py-1"><Input type="number" step="0.01" className="text-right tabular w-20" value={ln.discount_pct} onChange={e => updateLine(i, "discount_pct", e.target.value)} /></td>
                  <td className="py-1 text-right tabular">{fmtMoney(total)}</td>
                  <td><Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)}>×</Button></td>
                </tr>);
              })}</tbody>
            </table>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-4 pt-4 border-t border-border">
          <div><Label>Not</Label><Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="col-span-2" />
          <div className="space-y-2 text-right">
            <div><span className="label-caps mr-2">Ara Toplam:</span> <span className="tabular font-medium">{fmtMoney(totals.sub)}</span></div>
            <div><span className="label-caps mr-2">KDV:</span> <span className="tabular font-medium">{fmtMoney(totals.tax)}</span></div>
            <div><span className="label-caps mr-2">Genel Toplam:</span> <span className="tabular font-bold text-lg">{fmtMoney(totals.grand)}</span></div>
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={() => nav("/invoices")}>İptal</Button>
          <Button type="submit" disabled={busy} data-testid="invoice-submit"><Save className="h-4 w-4 mr-2" />Taslak Olarak Kaydet</Button>
        </div>
      </form>
    </div>
  );
}

// ---------------- SIMPLE MODULES (Banks, BankAccounts, CashRegisters, CreditCards, DBS, Cheques, Receipts, Payments, Journals, Accounts, TrialBalance, Subscriptions, Reminders, Attachments, Reports) ----------------

export function BanksPage() {
  return <ListPage testId="banks-list" title="Bankalar" endpoint="/banks"
    columns={[{key:"code",header:"Kod"},{key:"name",header:"Ad"},{key:"swift",header:"SWIFT"}]} />;
}

export function BankAccountsPage() {
  return <ListPage testId="bankacc-list" title="Banka Hesapları" endpoint="/bank-accounts"
    columns={[
      {key:"iban",header:"IBAN",render:r=><span className="font-mono text-xs">{r.iban}</span>},
      {key:"account_name",header:"Ad"},
      {key:"currency",header:"Para"},
      {key:"ledger_account_code",header:"Hesap Kodu"},
      {key:"opening_balance",header:"Açılış",align:"right",render:r=>fmtMoney(r.opening_balance)},
    ]} />;
}

// ===== KASA — Serbest gelir/gider hareketleri (yeni model) =====
export function CashPage() {
  const [sum, setSum] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [category, setCategory] = useState("");        // seçili kategori filtresi
  const [categories, setCategories] = useState([]);    // sistemdeki tüm distinct kategoriler
  const [debouncedRange, setDebouncedRange] = useState("");

  // Distinct kategorileri yükle (her yeni işlem sonrası refresh)
  const loadCategories = async () => {
    try {
      const { data } = await api.get("/cash-movements/categories");
      setCategories(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadCategories(); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = [];
      if (dateFrom) p.push(`date_from=${dateFrom}`);
      if (dateTo) p.push(`date_to=${dateTo}`);
      if (category) p.push(`category=${encodeURIComponent(category)}`);
      setDebouncedRange(p.join("&"));
    }, 250);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo, category]);

  const loadSum = async () => {
    try {
      // Summary hedef tarihi: dateTo verilmişse onu al, yoksa bugün
      const url = dateTo ? `/cash-movements/summary?target_date=${dateTo}` : "/cash-movements/summary";
      const { data } = await api.get(url); setSum(data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    loadSum();
    const iv = setInterval(loadSum, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateTo]);

  const summaryBar = sum ? (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-testid="cash-summary">
      <div className="surface p-4">
        <div className="label-caps text-emerald-700">{dateTo ? "Seçilen Gün Gelir" : "Bugünkü Gelir"}</div>
        <div className="text-2xl font-bold text-emerald-600 tabular" data-testid="cash-income-today">{fmtMoney(sum.income_today)}</div>
        <div className="text-xs text-muted-foreground">{sum.income_count} işlem</div>
      </div>
      <div className="surface p-4">
        <div className="label-caps text-red-700">{dateTo ? "Seçilen Gün Gider" : "Bugünkü Gider"}</div>
        <div className="text-2xl font-bold text-red-600 tabular" data-testid="cash-expense-today">{fmtMoney(sum.expense_today)}</div>
        <div className="text-xs text-muted-foreground">{sum.expense_count} işlem</div>
      </div>
      <div className="surface p-4">
        <div className="label-caps">Kasa Mevcudu</div>
        <div className={`text-2xl font-bold tabular ${sum.balance >= 0 ? "text-foreground" : "text-red-600"}`} data-testid="cash-balance">{fmtMoney(sum.balance)}</div>
        <div className="text-xs text-muted-foreground">Kümülatif · {fmtDate(sum.date)}</div>
      </div>
    </div>
  ) : null;

  return <ListPage testId="cash-list" title="Kasa" endpoint="/cash-movements"
    subtitle="Nakit gelir ve gider hareketleri"
    canDelete canEdit canDeleteAll
    importModule="cash-movements"
    importLabel="Kasa Hareketleri"
    summary={summaryBar}
    pdfSummary={sum ? [
      { label: dateTo ? "Seçilen Gün Gelir" : "Bugünkü Gelir",
        value: fmtMoney(sum.income_today),
        subtitle: `${sum.income_count} işlem`,
        color: "#059669" },
      { label: dateTo ? "Seçilen Gün Gider" : "Bugünkü Gider",
        value: fmtMoney(sum.expense_today),
        subtitle: `${sum.expense_count} işlem`,
        color: "#dc2626" },
      { label: "Kasa Mevcudu",
        value: fmtMoney(sum.balance),
        subtitle: `Kümülatif · ${fmtDate(sum.date)}`,
        color: (parseFloat(sum.balance) || 0) >= 0 ? "#111" : "#dc2626" },
    ] : undefined}
    onDataChange={() => { loadSum(); loadCategories(); }}
    listQuery={debouncedRange || undefined}
    extraActions={
      <div className="flex flex-wrap items-center gap-2">
        {/* Kategori filtresi */}
        <Select value={category || "_all"}
                onValueChange={v => setCategory(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-44" data-testid="cash-category-filter">
            <SelectValue placeholder="Kategori"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Tüm Kategoriler</SelectItem>
            {categories.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Tarih aralığı */}
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
               className="h-9 w-40" placeholder="Başlangıç"
               data-testid="cash-date-from"/>
        <span className="text-xs text-muted-foreground">—</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
               className="h-9 w-40" placeholder="Bitiş"
               data-testid="cash-date-to"/>
        {(dateFrom || dateTo || category) && (
          <Button variant="ghost" size="sm"
                  onClick={() => { setDateFrom(""); setDateTo(""); setCategory(""); }}
                  data-testid="cash-clear-date">Temizle</Button>
        )}
      </div>
    }
    deleteConfirm="Bu hareket silinsin mi?"
    createTitle="Yeni Kasa Hareketi"
    createFields={[
      { key: "type", label: "Hareket Türü", type: "select", required: true, default: "in",
        options: [{ value: "in", label: "Gelir" }, { value: "out", label: "Gider" }] },
      { key: "category", label: "Kategori",
        placeholder: "Örn: Satış, Kira, Fatura — mevcut kategorileri yazınca gelir",
        datalist: categories },
      { key: "description", label: "Açıklama", placeholder: "Örn: Merkez şubesi satış" },
      { key: "amount", label: "Tutar", type: "number", step: "0.01", required: true },
      { key: "date", label: "Tarih", type: "date", required: true, default: new Date().toISOString().slice(0,10) },
      { key: "currency", label: "Para Birimi", type: "select", default: "TRY",
        options: [{ value: "TRY", label: "₺ (TRY)" }, { value: "USD", label: "$ (USD)" }, { value: "EUR", label: "€ (EUR)" }] },
      { key: "notes", label: "Notlar", type: "textarea" },
    ]}
    columns={[
      {key:"date",header:"Tarih",sortable:true,render:r=>fmtDate(r.date)},
      {key:"type",header:"Tür",accessor:r=>r.type==="in"?"Gelir":"Gider",render:r=><StatusPill status={r.type} label={r.type==="in"?"Gelir":"Gider"}/>},
      {key:"category",header:"Kategori",render:r=>r.category||"—"},
      {key:"description",header:"Açıklama",render:r=>r.description||"—"},
      {key:"amount",header:"Tutar",align:"right",sortable:true,
       accessor:r=>{
         const sign = r.type==="in"?"+":"−";
         const cur = ({TRY:"₺",USD:"$",EUR:"€",GBP:"£"}[r.currency]||"₺");
         return `${sign} ${new Intl.NumberFormat("tr-TR",{minimumFractionDigits:2,maximumFractionDigits:2}).format(parseFloat(r.amount)||0)} ${cur}`;
       },
       render:r=>{
        const color = r.type==="in"?"text-emerald-600":"text-red-600";
        const sign = r.type==="in"?"+":"−";
        return <span className={`font-semibold ${color}`}>{sign} {fmtMoney(r.amount)}</span>;
      }},
      {key:"currency",header:"Para",accessor:r=>({TRY:"₺",USD:"$",EUR:"€",GBP:"£"}[r.currency]||r.currency||"₺"),render:r=>({TRY:"₺",USD:"$",EUR:"€",GBP:"£"}[r.currency]||r.currency||"₺")},
      {key:"notes",header:"Notlar",render:r=><span className="text-xs text-muted-foreground">{r.notes||"—"}</span>},
    ]}
    defaultSort={{ key: "date", dir: "desc" }}
  />;
}

// ===== KREDİ KARTLARI — Ekstre bazlı takip =====
export function CreditCardsPage() {
  return <ListPage testId="cards-list" title="Kredi Kartları" endpoint="/credit-cards"
    canDelete canEdit canDeleteAll
    importModule="credit-cards"
    deleteConfirm="Bu kartı silmek istediğinize emin misiniz?"
    createTitle="Yeni Kredi Kartı"
    createFields={[
      { key: "bank_name", label: "Banka", required: true, placeholder: "Örn: Garanti BBVA" },
      { key: "name", label: "Kart Adı", required: true, placeholder: "Örn: Bonus Business" },
      { key: "last_four", label: "Son 4 Hane", placeholder: "1234" },
      { key: "credit_limit", label: "Limit", type: "number", step: "0.01", default: "0" },
      { key: "statement_amount", label: "Ekstre Tutarı", type: "number", step: "0.01", default: "0" },
      { key: "statement_date", label: "Ekstre Kesim Tarihi", type: "date" },
      { key: "due_date", label: "Son Ödeme Tarihi", type: "date" },
      { key: "status", label: "Durum", type: "select", default: "aktif",
        options: [
          { value: "aktif", label: "Ödenecek" },
          { value: "odendi", label: "Ödendi" },
          { value: "gecikti", label: "Gecikti" },
          { value: "kapali", label: "Kapalı" },
        ] },
      { key: "notes", label: "Notlar", type: "textarea" },
    ]}
    columns={[
      {key:"bank_name",header:"Banka",sortable:true,render:r=><span className="font-medium">{r.bank_name || "—"}</span>},
      {key:"name",header:"Kart",sortable:true},
      {key:"last_four",header:"Son 4",render:r=>r.last_four ? `**** ${r.last_four}` : "—"},
      {key:"credit_limit",header:"Limit",align:"right",sortable:true,render:r=>fmtMoney(r.credit_limit)},
      {key:"statement_amount",header:"Ekstre",align:"right",sortable:true,render:r=><span className="font-semibold">{fmtMoney(r.statement_amount)}</span>},
      {key:"statement_date",header:"Kesim Tarihi",sortable:true,
       render:r=><DateCell value={r.statement_date}/>,
       // Kritik: ay içindeki gün numarasına göre sırala (yıl/ay dahil değil)
       sortAccessor:r=>r.statement_day || 32},
      {key:"due_date",header:"Son Ödeme",sortable:true,render:r=><DateCell value={r.due_date}/>},
      {key:"status",header:"Durum",render:r=><StatusPill status={r.status}/>},
      {key:"notes",header:"Notlar",
       render:r=>r.notes ? <span className="text-xs text-muted-foreground line-clamp-2 max-w-[240px] inline-block" title={r.notes}>{r.notes}</span> : "—"},
    ]}
    defaultSort={{ key: "statement_date", dir: "asc" }}
  />;
}

// ===== DBS — edit + delete-all + durum + tarih filtresi =====
export function DBSPage() {
  // Kullanıcı isteği: Varsayılan olarak "Bekleyen (Ödenecek)" görünsün.
  const [status, setStatus] = useState("beklemede");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const parts = [];
  if (status) parts.push(`status=${status}`);
  if (dueFrom) parts.push(`due_date_from=${dueFrom}`);
  if (dueTo) parts.push(`due_date_to=${dueTo}`);
  const listQuery = parts.join("&") || undefined;

  return <ListPage testId="dbs-list" title="DBS" endpoint="/dbs"
    subtitle="Doğrudan Borçlandırma — tedarikçi faturaları"
    canDelete canEdit canDeleteAll
    importModule="dbs"
    listQuery={listQuery}
    deleteConfirm="Bu DBS kaydını silmek istediğinize emin misiniz?"
    defaultSort={{ key: "due_date", dir: "asc" }}
    extraActions={
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={status || "_all"} onValueChange={v => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40" data-testid="dbs-status-filter">
            <SelectValue placeholder="Durum"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Tüm Durumlar</SelectItem>
            <SelectItem value="beklemede">Ödenecekler</SelectItem>
            <SelectItem value="odendi">Ödenenler</SelectItem>
            <SelectItem value="gecikti">Gecikenler</SelectItem>
            <SelectItem value="iptal">İptal</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground">Vade:</span>
          <Input type="date" value={dueFrom} onChange={e => setDueFrom(e.target.value)}
                 className="h-9 w-36" data-testid="dbs-due-from"
                 placeholder="Başlangıç"/>
          <span className="text-muted-foreground">→</span>
          <Input type="date" value={dueTo} onChange={e => setDueTo(e.target.value)}
                 className="h-9 w-36" data-testid="dbs-due-to"
                 placeholder="Bitiş"/>
          {(dueFrom || dueTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setDueFrom(""); setDueTo(""); }}
                    data-testid="dbs-due-clear" className="h-9 px-2">
              Temizle
            </Button>
          )}
        </div>
      </div>
    }
    createTitle="Yeni DBS Kaydı"
    createFields={[
      { key: "supplier_name", label: "Tedarikçi / Firma", required: true, placeholder: "Örn: ABC Tedarik" },
      { key: "invoice_no", label: "Fatura No", placeholder: "Örn: FT-2026-001" },
      { key: "bank_name", label: "Banka", placeholder: "Örn: Garanti BBVA" },
      { key: "amount", label: "Tutar", type: "number", step: "0.01", required: true },
      { key: "due_date", label: "Vade", type: "date" },
      { key: "status", label: "Durum", type: "select", default: "beklemede",
        options: [
          { value: "beklemede", label: "Ödenecek" },
          { value: "odendi", label: "Ödendi" },
          { value: "gecikti", label: "Gecikti" },
          { value: "iptal", label: "İptal" },
        ] },
      { key: "notes", label: "Notlar", type: "textarea" },
    ]}
    columns={[
      {key:"supplier_name",header:"Tedarikçi",sortable:true,render:r=><span className="font-medium">{r.supplier_name || "—"}</span>},
      {key:"invoice_no",header:"Fatura No",render:r=><span className="font-mono text-xs">{r.invoice_no || "—"}</span>},
      {key:"bank_name",header:"Banka",render:r=>r.bank_name || "—"},
      {key:"amount",header:"Tutar",align:"right",sortable:true,render:r=>fmtMoney(r.amount || r.max_amount)},
      {key:"due_date",header:"Vade",sortable:true,render:r=><DateCell value={r.due_date || r.expiry_date}/>},
      {key:"status",header:"Durum",render:r=><StatusPill status={r.status}/>},
      {key:"notes",header:"Notlar",render:r=><span className="text-xs text-muted-foreground">{r.notes || "—"}</span>},
    ]}
  />;
}

// ===== ÇEKLER — Alınan/Verilen ayrı sayfalar (direction prop) + Ciro workflow =====
// direction: "incoming" | "outgoing" | "endorsed" | "own" | "deposited"
export function ChequesPage({ direction }) {
  const [company, setCompany] = useState("");
  const [summary, setSummary] = useState(null);
  const [endorseRow, setEndorseRow] = useState(null);
  const [bulkEndorseCheques, setBulkEndorseCheques] = useState(null);
  const [bulkDepositCheques, setBulkDepositCheques] = useState(null);
  const [depositRow, setDepositRow] = useState(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const { toTRY } = useFxRates();

  const isIncoming = direction === "incoming";
  const isOutgoing = direction === "outgoing";
  const isEndorsed = direction === "endorsed";
  const isOwn      = direction === "own";
  const isDeposited = direction === "deposited";  // Bankaya Verilen Çekler
  const scope = isDeposited ? "Bankaya Verilen Çekler"
                : isEndorsed ? "Cirolanan Çekler"
                : isIncoming ? "Alınan Çekler"
                : isOwn ? "Kendi Çeklerimiz"
                : isOutgoing ? "Verilen Çekler" : "Çekler / Senetler";

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (company) params.set("company_id", company);
      if (isOwn) params.set("own", "true");
      const qs = params.toString();
      const url = qs ? `/cheques/summary?${qs}` : "/cheques/summary";
      const { data } = await api.get(url);
      setSummary(data);
    } catch { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); }, [company, isOwn]);

  // Kendi Çeklerimiz için duruma göre kırılım (Ödenecek / Ödendi / Gecikti / İade)
  const ownStatusOrder = [
    { key: "beklemede", label: "Ödenecek", color: "amber" },
    { key: "odendi",    label: "Ödendi",   color: "emerald" },
    { key: "gecikti",   label: "Gecikti",  color: "red" },
    { key: "iade",      label: "İade",     color: "slate" },
  ];
  const ownColorMap = {
    amber:   { text: "text-amber-700",   value: "text-amber-600" },
    emerald: { text: "text-emerald-700", value: "text-emerald-600" },
    red:     { text: "text-red-700",     value: "text-red-600" },
    slate:   { text: "text-slate-700",   value: "text-slate-700" },
  };

  // Cirolanan sayfasında summary gösterme. Kendi Çeklerimiz için status breakdown göster.
  let summaryBar = null;
  if (!isEndorsed && !isDeposited && summary) {
    // Backend by_currency: {TRY: {count, total, total_try}, USD: {...}, ...}
    // Sadece TRY dışı kayıt varsa küçük bir kırılım göstereceğiz.
    const nonTryBreakdown = (sec) => {
      const by = sec?.by_currency || {};
      return Object.entries(by)
        .filter(([c]) => c !== "TRY")
        .map(([c, v]) => `${fmtMoney(v.total, c)} → ${fmtMoney(v.total_try, "TRY")}`);
    };
    if (isOwn) {
      const sb = summary.outgoing?.status_breakdown || {};
      const detays = nonTryBreakdown(summary.outgoing);
      summaryBar = (
        <div className="surface p-4 mb-4" data-testid="cheques-summary">
          <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
            <div>
              <div className="label-caps text-red-700">Toplam (₺)</div>
              <div className="text-xl font-bold text-red-600 tabular" data-testid="cheques-own-total">{fmtMoney(summary.outgoing?.total, "TRY")}</div>
              <div className="text-xs text-muted-foreground">{summary.outgoing?.count || 0} çek</div>
            </div>
            {ownStatusOrder.map(({ key, label, color }) => {
              const st = sb[key] || { count: 0, total: "0.00" };
              const c = ownColorMap[color];
              return (
                <div key={key}>
                  <div className={`label-caps ${c.text}`}>{label}</div>
                  <div className={`text-xl font-bold tabular ${c.value}`}
                       data-testid={`cheques-own-${key}-total`}>{fmtMoney(st.total, "TRY")}</div>
                  <div className="text-xs text-muted-foreground">{st.count || 0} çek</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      const incDetays = nonTryBreakdown(summary.incoming);
      const outDetays = nonTryBreakdown(summary.outgoing);
      summaryBar = (
        <div className="surface p-4 mb-4" data-testid="cheques-summary">
          <div className={`grid gap-4 ${isIncoming || isOutgoing ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"}`}>
            {(!isOutgoing) && (
              <>
                <div>
                  <div className="label-caps text-emerald-700">Alınan Toplam (₺)</div>
                  <div className="text-xl font-bold text-emerald-600 tabular" data-testid="cheques-incoming-total">{fmtMoney(summary.incoming?.total, "TRY")}</div>
                  <div className="text-xs text-muted-foreground">{summary.incoming?.count || 0} çek</div>
                </div>
                <div>
                  <div className="label-caps text-emerald-700">Alınan Ort. Vade</div>
                  <div className="text-lg font-semibold tabular" data-testid="cheques-incoming-avg">{summary.incoming?.avg_due_date || "—"}</div>
                  <div className="text-xs text-muted-foreground">Ağırlıklı</div>
                </div>
              </>
            )}
            {(!isIncoming) && (
              <>
                <div>
                  <div className="label-caps text-red-700">Verilen Toplam (₺)</div>
                  <div className="text-xl font-bold text-red-600 tabular" data-testid="cheques-outgoing-total">{fmtMoney(summary.outgoing?.total, "TRY")}</div>
                  <div className="text-xs text-muted-foreground">{summary.outgoing?.count || 0} çek</div>
                </div>
                <div>
                  <div className="label-caps text-red-700">Verilen Ort. Vade</div>
                  <div className="text-lg font-semibold tabular" data-testid="cheques-outgoing-avg">{summary.outgoing?.avg_due_date || "—"}</div>
                  <div className="text-xs text-muted-foreground">Ağırlıklı</div>
                </div>
              </>
            )}
          </div>
          {(incDetays.length + outDetays.length) > 0 && (
            <div className="mt-3 pt-3 border-t text-xs text-muted-foreground space-y-1"
                 data-testid="cheques-fx-detay">
              {incDetays.length > 0 && !isOutgoing && (
                <div><span className="font-semibold text-emerald-700 mr-2">Alınan (Para birimi):</span>{incDetays.join(" · ")}</div>
              )}
              {outDetays.length > 0 && !isIncoming && (
                <div><span className="font-semibold text-red-700 mr-2">Verilen (Para birimi):</span>{outDetays.join(" · ")}</div>
              )}
            </div>
          )}
        </div>
      );
    }
  }

  // listQuery: direction + company + endorsed özel modu + own filtresi + bankaya verilen
  const parts = [];
  if (isDeposited) {
    parts.push("direction=incoming");
    parts.push("deposited=true");
    parts.push("exclude_deposited=false");  // bu view'da bankaya verilenler görünsün
  } else if (isEndorsed) {
    parts.push("only_endorsed=true");
    parts.push("direction=incoming");
    parts.push("exclude_deposited=false");  // bankaya verilmişse de cirolanan olabilir
    // Cirolanan'da vadesi geçmiş = due_date < bugün (bugün hariç)
    if (overdueOnly) {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const iso = `${y.getFullYear()}-${String(y.getMonth()+1).padStart(2,"0")}-${String(y.getDate()).padStart(2,"0")}`;
      parts.push(`to_date=${iso}`);
    }
  } else if (isOwn) {
    parts.push("direction=outgoing");
    parts.push("own=true");
  } else if (direction) {
    parts.push(`direction=${direction}`);
  }
  if (company)   parts.push(`company_id=${company}`);
  if (statusFilter) parts.push(`status=${statusFilter}`);
  const listQuery = parts.length > 0 ? parts.join("&") : undefined;

  const cols = [
    {key:"cheque_no",header:"Seri No",sortable:true,render:r=><span className="font-mono">{r.cheque_no}</span>},
    {key:"company_id",header:"Şirket",render:r=>({
      "hasyapi-ltd":"Hasyapı LTD.","mehmet-has":"Mehmet Has"}[r.company_id]||r.company_id||"—")},
    {key:"bank_name",header:"Banka",render:r=>r.bank_name || "—"},
    {key:"drawer",header:"Keşideci",render:r=>r.drawer || "—"},
    // Verilen çeklerde "Lehdar" → "Verilen Firma" olarak göster
    {key:"beneficiary",
     header: isOwn ? "Alıcı Firma" : (isOutgoing ? "Verilen Firma" : "Lehdar"),
     render:r=>r.beneficiary || "—"},
    {key:"amount",header:"Tutar",align:"right",sortable:true,render:r=>(
      <span className="whitespace-nowrap">{fmtAmtTL(r.amount, r.currency, toTRY)}</span>
    )},
    {key:"due_date",header:"Vade",sortable:true,render:r=>{
      const iso = String(r.due_date || "").slice(0,10);
      const today = new Date().toISOString().slice(0,10);
      const overdue = iso && iso < today && r.status !== "odendi" && r.status !== "tahsil_oldu";
      const todayHit = iso === today;
      let cls = "";
      if (overdue) cls = "text-red-600 font-semibold";
      else if (todayHit) cls = "text-orange-600 font-semibold";
      return (
        <span className={cls}>
          {fmtDate(r.due_date)}
          {overdue && <span className="ml-1 text-[10px] uppercase font-bold">· Vadesi Geçmiş</span>}
          {todayHit && !overdue && <span className="ml-1 text-[10px] uppercase font-bold">· Bugün</span>}
        </span>
      );
    }},
    {key:"status",header:"Durum",render:r=><StatusPill status={r.status} label={
      // Alınan Çekler için "beklemede" statüsü UI'da "Portföyde" olarak gösterilir.
      // Verilen/Kendi/Cirolanan için otomatik "Ödenecek" olarak kalır.
      r.direction === "incoming" && r.status === "beklemede" ? "Portföyde"
      : ({portfolio:"Portföyde",handed_over:"Ciro Edildi",cleared:"Tahsil Oldu",
          bounced:"Karşılıksız",returned:"İade",cancelled:"İptal"}[r.status])
    }/>},
    {key:"notes",header:"Notlar",render:r=><span className="text-xs text-muted-foreground">{r.notes || "—"}</span>},
  ];
  // Cirolanan çekler sayfasında "Ciro Edilen Yer" kolonu ekle
  if (isEndorsed) {
    cols.splice(5, 0, {key:"endorsed_to",header:"Ciro Edilen Yer",
      render:r=><span className="font-medium text-purple-700">{r.endorsed_to || "—"}</span>});
    cols.splice(6, 0, {key:"endorsed_at",header:"Ciro Tarihi",
      render:r=>fmtDate(r.endorsed_at)});
  }
  // Bankaya Verilen Çekler sayfasında tahsile verilen banka + veriliş/beklenen tahsil tarihi
  if (isDeposited) {
    cols.splice(5, 0, {key:"deposit_bank_name",header:"Tahsile Verilen Banka",
      render:r=><span className="font-medium text-blue-700">{r.deposit_bank_name || "—"}</span>});
    cols.splice(6, 0, {key:"deposited_at",header:"Bankaya Veriliş",
      render:r=>fmtDate(r.deposited_at)});
    cols.splice(7, 0, {key:"expected_collection_date",header:"Tahmini Tahsil",
      render:r=><span className="text-emerald-700">{fmtDate(r.expected_collection_date) || "—"}</span>});
    // Ekrana sığması için daha az kritik kolonları gizle (Notlar, Keşideci, Banka)
    const HIDE_FOR_DEPOSITED = new Set(["notes", "drawer", "bank_name"]);
    for (let i = 0; i < cols.length; i++) {
      if (HIDE_FOR_DEPOSITED.has(cols[i].key)) {
        cols[i] = { ...cols[i], hiddenByDefault: true };
      }
    }
  }
  if (!isIncoming && !isOutgoing && !isEndorsed && !isOwn && !isDeposited) {
    // Ana çekler sayfasında Tür kolonu göster (Bankaya Verilen'de gereksiz)
    cols.splice(1, 0, {key:"direction",header:"Tür",
      render:r=><StatusPill status={r.direction} label={r.direction==="incoming"?"Alınan":"Verilen"}/>});
  }

  // Row actions:
  // - Incoming (Alınan): "C" (Cirola) butonu
  // - Endorsed (Cirolanan): "Geri Al" butonu → çeki tekrar Alınan'a gönderir
  const doUnendorse = async (row) => {
    if (!window.confirm(`"${row.cheque_no}" numaralı çeki cirolamadan geri almak istiyor musunuz?\n\nÇek tekrar Alınan Çekler listesine eklenecek.`)) return;
    try {
      await api.post(`/cheques/${row.id}/unendorse`);
      toast.success("Ciro geri alındı");
      // Sayfayı yenile — SWR/list otomatik güncellemesi için onDataChange tetikleyicisi ListPage'de var
      window.dispatchEvent(new CustomEvent("cheques:refresh"));
      loadSummary();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const extraRowActions = isIncoming ? (row) => {
    if (["ciro_edildi","cancelled","iptal","tahsil_oldu","iade"].includes(row.status)) return null;
    return (
      <div className="flex gap-1">
        <Button variant="ghost" size="icon" title="Cirola"
                onClick={() => setEndorseRow(row)}
                data-testid={`cheques-endorse-${row.id}`}>
          <span className="text-purple-600 font-bold text-sm">C</span>
        </Button>
        <Button variant="ghost" size="icon" title="Bankaya Ver"
                onClick={() => setDepositRow(row)}
                data-testid={`cheques-deposit-${row.id}`}>
          <span className="text-blue-600 font-bold text-sm">B</span>
        </Button>
      </div>
    );
  } : isEndorsed ? (row) => (
    <Button variant="ghost" size="icon" title="Ciroyu Geri Al"
            onClick={() => doUnendorse(row)}
            data-testid={`cheques-unendorse-${row.id}`}>
      <span className="text-amber-600 font-bold text-sm">↺</span>
    </Button>
  ) : isDeposited ? (row) => (
    <Button variant="ghost" size="icon" title="Bankadan Geri Al"
            onClick={async () => {
              if (!window.confirm(`"${row.cheque_no}" numaralı çeki bankadan geri almak istiyor musunuz?`)) return;
              try {
                await api.post(`/cheques/${row.id}/undeposit`);
                toast.success("Çek geri alındı");
                window.dispatchEvent(new CustomEvent("cheques:refresh"));
                loadSummary();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            data-testid={`cheques-undeposit-${row.id}`}>
      <span className="text-amber-600 font-bold text-sm">↺</span>
    </Button>
  ) : undefined;

  return (
    <>
      <ListPage testId="cheques-list" title={scope}
        subtitle={isEndorsed ? "Ciro edilmiş çekler (Alınan listesinden çıkarıldı)"
                 : isIncoming ? "Portföyde bulunan alınan çekler"
                 : isOwn ? "Kendi firmalarımıza kestiğimiz çekler"
                 : isOutgoing ? "Verilen çekler ve cirolar" : undefined}
        endpoint="/cheques"
        canDelete canEdit canDeleteAll
        importModule="cheques"
        importLabel={scope}
        deleteConfirm="Bu çeki silmek istediğinize emin misiniz?"
        summary={summaryBar}
        defaultSort={{ key: "due_date", dir: "asc" }}
        {...(isIncoming ? {
          // eslint-disable-next-line react/no-unstable-nested-components
          bulkExtras: (ids, rows, clearSel) => (
            <>
              <Button variant="outline" size="sm"
                      onClick={() => { setBulkEndorseCheques(rows); clearSel(); }}
                      data-testid="cheques-bulk-endorse-btn"
                      className="border-purple-400 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30">
                <span className="font-bold mr-1">C</span> Toplu Cirola ({ids.length})
              </Button>
              <Button variant="outline" size="sm"
                      onClick={() => { setBulkDepositCheques(rows); clearSel(); }}
                      data-testid="cheques-bulk-deposit-btn"
                      className="border-blue-400 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30">
                <span className="font-bold mr-1">B</span> Toplu Bankaya Ver ({ids.length})
              </Button>
            </>
          ),
        } : {})}
        pdfSummary={(!isEndorsed && summary) ? (
          isOwn ? [
            { label: "Toplam", value: fmtMoney(summary.outgoing?.total),
              subtitle: `${summary.outgoing?.count || 0} çek`, color: "#dc2626" },
            ...ownStatusOrder.map(({ key, label, color }) => {
              const sb = summary.outgoing?.status_breakdown || {};
              const st = sb[key] || { count: 0, total: "0.00" };
              const hex = { amber: "#d97706", emerald: "#059669", red: "#dc2626", slate: "#475569" }[color];
              return { label, value: fmtMoney(st.total),
                       subtitle: `${st.count || 0} çek`, color: hex };
            })
          ] : [
          ...((!isOutgoing) ? [{
            label: "Alınan Toplam",
            value: fmtMoney(summary.incoming?.total),
            subtitle: `${summary.incoming?.count || 0} çek`,
            color: "#059669",
          }, {
            label: "Alınan Ort. Vade",
            value: summary.incoming?.avg_due_date || "—",
            subtitle: "Ağırlıklı",
            color: "#059669",
          }] : []),
          ...((!isIncoming) ? [{
            label: "Verilen Toplam",
            value: fmtMoney(summary.outgoing?.total),
            subtitle: `${summary.outgoing?.count || 0} çek`,
            color: "#dc2626",
          }, {
            label: "Verilen Ort. Vade",
            value: summary.outgoing?.avg_due_date || "—",
            subtitle: "Ağırlıklı",
            color: "#dc2626",
          }] : []),
        ]) : undefined}
        onDataChange={loadSummary}
        extraRowActions={extraRowActions}
        extraActions={
          <div className="flex flex-wrap gap-2 items-center">
            {isEndorsed && (
              <Select value={overdueOnly ? "overdue" : "all"} onValueChange={v => setOverdueOnly(v === "overdue")}>
                <SelectTrigger className="h-9 w-52" data-testid="cheques-overdue-filter">
                  <SelectValue placeholder="Vade Durumu"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Cirolanan Çekler</SelectItem>
                  <SelectItem value="overdue">Sadece Vadesi Geçmiş</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 w-44" data-testid="cheques-status-filter">
                <SelectValue placeholder="Durum"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tümünü Göster</SelectItem>
                {(isOwn || isOutgoing) ? (
                  <>
                    <SelectItem value="beklemede">Ödenecek</SelectItem>
                    <SelectItem value="odendi">Ödendi</SelectItem>
                    <SelectItem value="gecikti">Gecikti</SelectItem>
                    <SelectItem value="iade">İade</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="beklemede">Portföyde</SelectItem>
                    <SelectItem value="tahsil_oldu">Tahsil Oldu</SelectItem>
                    <SelectItem value="gecikti">Gecikti</SelectItem>
                    <SelectItem value="iade">İade</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <Select value={company || "_all"} onValueChange={(v) => setCompany(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 w-52" data-testid="cheques-company-filter">
                <SelectValue placeholder="Şirket"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tüm Şirketler</SelectItem>
                <SelectItem value="hasyapi-ltd">Hasyapı LTD. ŞTİ.</SelectItem>
                <SelectItem value="mehmet-has">Mehmet Has</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        createTitle={`Yeni ${isIncoming ? "Alınan Çek" : isOwn ? "Kendi Çekimiz" : isOutgoing ? "Verilen Çek" : "Çek"}`}
        createFields={[
          ...((direction && !isOwn) ? [] : (!direction ? [{ key: "direction", label: "Tür", type: "select", required: true, default: "incoming",
            options: [{ value: "incoming", label: "Alınan" }, { value: "outgoing", label: "Verilen" }] }] : [])),
          { key: "company_id", label: isOwn ? "Kesildiği Şirket" : "Şirket", type: "select", required: true, default: "hasyapi-ltd",
            options: [
              { value: "hasyapi-ltd", label: "Hasyapı LTD. ŞTİ." },
              { value: "mehmet-has", label: "Mehmet Has" },
            ] },
          { key: "cheque_no", label: "Çek / Seri No", required: true, placeholder: "Örn: 0011234" },
          { key: "bank_name", label: "Banka", placeholder: "Örn: Ziraat Bankası" },
          { key: "drawer", label: "Keşideci",
            placeholder: isOwn ? "Çeki kesen yetkili" : "Çeki düzenleyen",
            ...(isIncoming ? { remember: "cheque_drawer" } : {}) },
          { key: "beneficiary", label: isOwn ? "Alıcı Firma (Kendi Firmamız)" : "Lehdar",
            placeholder: isOwn ? "Örn: Mehmet Has, Hasyapı LTD." : "Çekin verildiği kişi/firma",
            ...(isIncoming ? { remember: "cheque_beneficiary" } : {}) },
          { key: "amount", label: "Tutar", type: "number", step: "0.01", required: true },
          { key: "currency", label: "Para Birimi", type: "select", default: "TRY",
            options: [
              { value: "TRY", label: "₺ (TRY)" },
              { value: "USD", label: "$ (USD)" },
              { value: "EUR", label: "€ (EUR)" },
              { value: "GBP", label: "£ (GBP)" },
              { value: "CHF", label: "CHF" },
            ] },
          { key: "due_date", label: "Vade", type: "date", required: true },
          { key: "status", label: "Durum", type: "select", default: "beklemede",
            options: (isOwn || isOutgoing)
              ? [
                  { value: "beklemede", label: "Ödenecek" },
                  { value: "odendi",    label: "Ödendi" },
                  { value: "gecikti",   label: "Gecikti" },
                  { value: "iade",      label: "İade" },
                ]
              : [
                  { value: "beklemede",   label: "Portföyde" },
                  { value: "tahsil_oldu", label: "Tahsil Oldu" },
                  { value: "gecikti",     label: "Gecikti" },
                  { value: "iade",        label: "İade" },
                ] },
          { key: "notes", label: "Notlar", type: "textarea" },
          ...(isEndorsed ? [
            { key: "endorsed_to", label: "Ciro Edilen Yer (Firma)", placeholder: "Örn: TestAlıcı" },
            { key: "endorsed_at", label: "Ciro Tarihi", type: "date" },
          ] : []),
        ]}
        createPayload={(f) => {
          if (isOwn) return { ...f, direction: "outgoing", own: true };
          if (direction) return { ...f, direction };
          return f;
        }}
        listQuery={listQuery}
        columns={cols}
      />
      {endorseRow && (
        <EndorseDialog cheque={endorseRow}
                       onClose={() => setEndorseRow(null)}
                       onDone={() => { setEndorseRow(null); window.location.reload(); }}/>
      )}
      {bulkEndorseCheques && bulkEndorseCheques.length > 0 && (
        <EndorseDialog cheques={bulkEndorseCheques}
                       onClose={() => setBulkEndorseCheques(null)}
                       onDone={() => { setBulkEndorseCheques(null); window.location.reload(); }}/>
      )}
      {depositRow && (
        <DepositToBankDialog cheque={depositRow}
                              onClose={() => setDepositRow(null)}
                              onDone={() => { setDepositRow(null); window.location.reload(); }}/>
      )}
      {bulkDepositCheques && bulkDepositCheques.length > 0 && (
        <DepositToBankDialog cheques={bulkDepositCheques}
                              onClose={() => setBulkDepositCheques(null)}
                              onDone={() => { setBulkDepositCheques(null); window.location.reload(); }}/>
      )}
    </>
  );
}

// Alınan çeki cirolayan dialog — tek veya çoklu çek destekli
// Props: `cheque` (tek çek) VEYA `cheques` (çek dizisi — toplu ciro için)
function EndorseDialog({ cheque, cheques, onClose, onDone }) {
  const items = cheques && cheques.length > 0 ? cheques : (cheque ? [cheque] : []);
  const isBulk = items.length > 1;
  const [to, setTo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const totalAmount = items.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const save = async () => {
    if (!to.trim()) { toast.error("Ciro edilen yer zorunlu"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    try {
      // Sırayla cirola (paralel yaparsak backend'de race condition riski)
      for (const c of items) {
        try {
          await api.post(`/cheques/${c.id}/endorse`, {
            endorsed_to: to.trim(),
            endorsement_date: date,
            note: note.trim() || undefined,
          });
          ok++;
        } catch { fail++; }
      }
      if (ok > 0) {
        toast.success(
          isBulk
            ? `${ok} çek cirolandı → ${to}${fail ? ` (${fail} hata)` : ""}`
            : `Çek cirolandı → ${to}. Verilen çekler sayfasına aktarıldı.`
        );
      } else {
        toast.error("Ciro işlemi başarısız oldu");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="cheque-endorse-dialog">
        <DialogHeader>
          <DialogTitle>{isBulk ? `${items.length} Çeki Toplu Cirola` : "Çeki Cirola"}</DialogTitle>
          <DialogDescription>
            {isBulk ? (
              <>
                <span className="font-semibold">{items.length} çek</span> — toplam <span className="font-mono font-semibold">{fmtMoney(totalAmount)}</span>
                <br/>Hepsi tek seferde cirolanacak ve <b>Verilen Çekler</b> sayfasına aktarılacak.
              </>
            ) : (
              <>
                <span className="font-mono">{items[0]?.cheque_no}</span> — {fmtMoney(items[0]?.amount)} — Vade: {fmtDate(items[0]?.due_date)}
                <br/>Ciro sonrası çek &quot;Ciro Edildi&quot; durumuna alınır ve otomatik olarak <b>Verilen Çekler</b> sayfasına yeni bir kayıt olarak eklenir.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {isBulk && (
          <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs bg-muted/30">
            {items.slice(0, 20).map(c => (
              <div key={c.id} className="flex justify-between py-0.5 border-b last:border-0">
                <span className="font-mono">{c.cheque_no}</span>
                <span>{fmtMoney(c.amount)}</span>
                <span className="text-muted-foreground">{fmtDate(c.due_date)}</span>
              </div>
            ))}
            {items.length > 20 && (
              <div className="text-center text-muted-foreground pt-1">
                ... {items.length - 20} çek daha
              </div>
            )}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Ciro Edilen Yer *</Label>
            <Input value={to} onChange={e => setTo(e.target.value)}
                   placeholder="Örn: XYZ Ltd, Ahmet Yılmaz"
                   data-testid="cheque-endorse-to"/>
          </div>
          <div>
            <Label className="text-xs">Ciro Tarihi</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                   data-testid="cheque-endorse-date"/>
          </div>
          <div>
            <Label className="text-xs">Not (opsiyonel)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      data-testid="cheque-endorse-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={busy} data-testid="cheque-endorse-save">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            {isBulk ? `${items.length} Çeki Cirola` : "Cirola"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChequeDetailPage() {
  const { id } = useParams(); const nav = useNavigate();
  const [ch, setCh] = useState(null);
  useEffect(() => { api.get(`/cheques?limit=500`).then(({data}) => setCh(data.find(c => c.id === id))); }, [id]);
  if (!ch) return <div className="p-6">Yükleniyor...</div>;
  return (
    <div data-testid="cheque-detail">
      <PageHeader title={`Çek ${ch.internal_no}`}
        subtitle={`${ch.direction==="incoming"?"Alınan":"Verilen"} · Vade: ${fmtDate(ch.due_date)}`}
        actions={<Button variant="outline" onClick={() => nav("/cheques")}><ArrowLeft className="h-4 w-4 mr-2"/>Geri</Button>} />
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="surface p-5"><div className="label-caps">Durum</div><div className="mt-2"><StatusPill status={ch.status}/></div></div>
        <div className="surface p-5"><div className="label-caps">Tutar</div><div className="text-2xl font-bold tabular mt-2">{fmtMoney(ch.amount)}</div></div>
        <div className="surface p-5"><div className="label-caps">Çek No</div><div className="text-lg font-mono mt-2">{ch.cheque_no}</div></div>
        <div className="surface p-5"><div className="label-caps">Keşideci</div><div className="text-lg mt-2">{ch.drawer||"—"}</div></div>
      </div>
      <div className="surface p-5">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><ScrollText className="h-4 w-4"/>Durum Geçmişi</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border"><th className="label-caps py-2 text-left">Tarih</th><th className="label-caps py-2 text-left">Geçiş</th><th className="label-caps py-2 text-left">Kullanıcı</th><th className="label-caps py-2 text-left">Not</th></tr></thead>
          <tbody>{ch.status_history?.map((h,i)=>(<tr key={i} className="border-b border-border"><td className="py-1.5 tabular">{fmtDateTime(h.at)}</td><td className="py-1.5">{h.from||"—"} → <strong>{h.to}</strong></td><td className="py-1.5">{h.by}</td><td className="py-1.5">{h.note||"—"}</td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

export function ReceiptsPage() {
  return <ListPage testId="receipts-list" title="Tahsilatlar" endpoint="/receipts"
    columns={[
      {key:"receipt_no",header:"Tahsilat No",render:r=><span className="font-mono">{r.receipt_no}</span>},
      {key:"date",header:"Tarih",render:r=>fmtDate(r.date)},
      {key:"method",header:"Yöntem"},
      {key:"amount",header:"Tutar",align:"right",render:r=>fmtMoney(r.amount)},
    ]} />;
}

export function PaymentsPage() {
  return <ListPage testId="payments-list" title="Ödemeler" endpoint="/payments"
    columns={[
      {key:"payment_no",header:"Ödeme No",render:r=><span className="font-mono">{r.payment_no}</span>},
      {key:"date",header:"Tarih",render:r=>fmtDate(r.date)},
      {key:"method",header:"Yöntem"},
      {key:"amount",header:"Tutar",align:"right",render:r=>fmtMoney(r.amount)},
    ]} />;
}

export function JournalsPage() {
  return <ListPage testId="journals-list" title="Yevmiye Defteri" endpoint="/ledger/journals"
    columns={[
      {key:"journal_no",header:"Yevmiye No",render:r=><span className="font-mono">{r.journal_no}</span>},
      {key:"posted_at",header:"Tarih",render:r=>fmtDateTime(r.posted_at)},
      {key:"source_type",header:"Kaynak"},
      {key:"description",header:"Açıklama"},
      {key:"total_debit",header:"Borç",align:"right",render:r=>fmtMoney(r.total_debit)},
      {key:"total_credit",header:"Alacak",align:"right",render:r=>fmtMoney(r.total_credit)},
      statusCol(),
    ]} />;
}

export function AccountsPage() {
  return <ListPage testId="accounts-list" title="Hesap Planı" subtitle="Chart of Accounts (TDHP)" endpoint="/ledger/accounts"
    columns={[
      {key:"code",header:"Kod",render:r=><span className="font-mono font-medium">{r.code}</span>},
      {key:"name",header:"Ad"},
      {key:"type",header:"Tür"},
      {key:"normal_side",header:"Normal Taraf"},
      {key:"parent_code",header:"Üst Hesap"},
    ]} />;
}

export function TrialBalancePage() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/reports/trial-balance").then(({data}) => { setRows(data); setLoading(false); }); }, []);
  return (
    <div data-testid="trial-balance-page">
      <PageHeader title="Mizan" subtitle="Trial Balance — hesap bazında borç/alacak/bakiye" />
      <DataTable data={rows} loading={loading} title="Mizan" testId="trial-balance"
        columns={[
          {key:"code",header:"Kod",render:r=><span className="font-mono">{r.code}</span>},
          {key:"name",header:"Hesap Adı"},
          {key:"type",header:"Tür"},
          {key:"debit",header:"Borç",align:"right",render:r=>fmtMoney(r.debit)},
          {key:"credit",header:"Alacak",align:"right",render:r=>fmtMoney(r.credit)},
          {key:"balance",header:"Bakiye",align:"right",render:r=>fmtMoney(r.balance)},
        ]} />
    </div>
  );
}

export function SubscriptionsPage() {
  const [category, setCategory] = useState("");
  // Kullanıcı isteği: Sayfaya ilk girildiğinde varsayılan olarak "Ödenecekler" görünsün.
  const [status, setStatus] = useState("beklemede");
  const parts = [];
  if (category) parts.push(`category=${category}`);
  if (status) parts.push(`status=${status}`);
  const listQuery = parts.join("&") || undefined;

  return <ListPage testId="subs-list" title="Abonelikler" endpoint="/subscriptions"
    subtitle="Elektrik, su, internet, telefon, doğalgaz vb. periyodik faturalar"
    canDelete canEdit canDeleteAll
    importModule="subscriptions"
    listQuery={listQuery}
    defaultSort={{ key: "next_run", dir: "asc" }}
    extraActions={
      <div className="flex gap-2">
        <Select value={category || "_all"} onValueChange={v => setCategory(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40" data-testid="subs-cat-filter"><SelectValue placeholder="Tür"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Tüm Türler</SelectItem>
            <SelectItem value="elektrik">Elektrik</SelectItem>
            <SelectItem value="su">Su</SelectItem>
            <SelectItem value="internet">İnternet</SelectItem>
            <SelectItem value="telefon">Telefon</SelectItem>
            <SelectItem value="dogalgaz">Doğalgaz</SelectItem>
            <SelectItem value="diger">Diğer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status || "_all"} onValueChange={v => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40" data-testid="subs-status-filter"><SelectValue placeholder="Durum"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Tümü</SelectItem>
            <SelectItem value="beklemede">Ödenecekler</SelectItem>
            <SelectItem value="odendi">Ödenenler</SelectItem>
            <SelectItem value="gecikti">Gecikenler</SelectItem>
            <SelectItem value="iptal">İptal</SelectItem>
          </SelectContent>
        </Select>
      </div>
    }
    deleteConfirm="Bu aboneliği silmek istediğinize emin misiniz?"
    createTitle="Yeni Abonelik"
    createFields={[
      { key: "name", label: "Abonelik Adı", required: true, placeholder: "Örn: Ofis Elektriği" },
      { key: "category", label: "Tür", type: "select", required: true, default: "elektrik",
        options: [
          { value: "elektrik", label: "Elektrik" },
          { value: "su", label: "Su" },
          { value: "internet", label: "İnternet" },
          { value: "telefon", label: "Telefon" },
          { value: "dogalgaz", label: "Doğalgaz" },
          { value: "diger", label: "Diğer" },
        ] },
      { key: "provider", label: "Sağlayıcı", placeholder: "Örn: BEDAŞ, Türk Telekom" },
      { key: "subscriber_no", label: "Abone No", placeholder: "Örn: 12345678" },
      { key: "amount", label: "Tutar", type: "number", step: "0.01", required: true },
      { key: "next_run", label: "Son Ödeme Tarihi", type: "date", required: true },
      { key: "status", label: "Durum", type: "select", default: "beklemede",
        options: [
          { value: "beklemede", label: "Ödenecek" },
          { value: "odendi", label: "Ödendi" },
          { value: "gecikti", label: "Gecikti" },
          { value: "iptal", label: "İptal" },
        ] },
      { key: "notes", label: "Notlar", type: "textarea" },
    ]}
    columns={[
      {key:"name",header:"Ad",sortable:true,render:r=><span className="font-medium">{r.name}</span>},
      {key:"category",header:"Tür",render:r=><StatusPill status={r.category || r.type} tone="info" label={
        {elektrik:"Elektrik",su:"Su",internet:"İnternet",telefon:"Telefon",
         dogalgaz:"Doğalgaz",diger:"Diğer"}[r.category] || r.type || "—"
      }/>},
      {key:"provider",header:"Sağlayıcı",render:r=>r.provider || "—"},
      {key:"subscriber_no",header:"Abone No",render:r=><span className="font-mono text-xs">{r.subscriber_no || "—"}</span>},
      {key:"amount",header:"Tutar",align:"right",sortable:true,render:r=>fmtMoney(r.amount)},
      {key:"next_run",header:"Son Ödeme",sortable:true,render:r=><DateCell value={r.next_run}/>},
      {key:"status",header:"Durum",render:r=><StatusPill status={r.status}/>},
      {key:"notes",header:"Notlar",render:r=><span className="text-xs text-muted-foreground">{r.notes || "—"}</span>},
    ]}
    />;
}

export function RemindersPage() {
  return <ListPage testId="reminders-list" title="Hatırlatmalar" endpoint="/reminders"
    columns={[
      {key:"title",header:"Başlık"},
      {key:"due_at",header:"Vade",render:r=>fmtDateTime(r.due_at)},
      {key:"priority",header:"Öncelik",render:r=><StatusPill status={r.priority==="high"?"danger":r.priority==="normal"?"info":"neutral"} label={r.priority}/>},
      statusCol(),
    ]} />;
}

export function AttachmentsPage() {
  return <ListPage testId="attachments-list" title="Dosya Ekleri" endpoint="/attachments"
    columns={[
      {key:"filename",header:"Dosya"},
      {key:"mime_type",header:"Tip"},
      {key:"size_bytes",header:"Boyut",align:"right",render:r=>`${(r.size_bytes/1024).toFixed(1)} KB`},
      {key:"linked_entity",header:"Bağlı"},
      {key:"created_at",header:"Yüklenme",render:r=>fmtDateTime(r.created_at)},
    ]} />;
}

// ---------------- REPORTS ----------------
export function AgingReportPage() {
  const [data, setData] = useState([]); const [type, setType] = useState("receivable"); const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get(`/reports/aging?type=${type}`).then(({data}) => { setData(data); setLoading(false); }); }, [type]);
  return (
    <div data-testid="aging-report">
      <PageHeader title="Yaşlandırma Raporu" subtitle="Vade geçen alacak/borç yaşlandırma"
        actions={<Select value={type} onValueChange={setType}><SelectTrigger className="w-40"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="receivable">Alacak</SelectItem><SelectItem value="payable">Borç</SelectItem></SelectContent></Select>} />
      <div className="grid md:grid-cols-4 gap-4">
        {data.map(b => (
          <div key={b.bucket} className="surface p-5">
            <div className="label-caps">{b.bucket} gün</div>
            <div className="text-2xl font-heading font-bold mt-2 tabular">{fmtMoney(b.total)}</div>
            <div className="text-xs text-muted-foreground mt-1">{b.count} kayıt</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function VatReportPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/vat-summary").then(({data}) => setData(data)); }, []);
  return (
    <div data-testid="vat-report">
      <PageHeader title="KDV Özeti" subtitle="Hesaplanan (391) - İndirilecek (191)" />
      {data && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="surface p-5"><div className="label-caps">Hesaplanan KDV (391)</div><div className="text-3xl font-heading font-bold mt-2 tabular">{fmtMoney(data.output_vat_391)}</div></div>
          <div className="surface p-5"><div className="label-caps">İndirilecek KDV (191)</div><div className="text-3xl font-heading font-bold mt-2 tabular">{fmtMoney(data.input_vat_191)}</div></div>
          <div className="surface p-5"><div className="label-caps">Net Ödenecek</div><div className="text-3xl font-heading font-bold mt-2 tabular">{fmtMoney(data.net_vat_payable)}</div></div>
        </div>
      )}
    </div>
  );
}

// Çeki bankaya veren dialog — tek veya çoklu çek destekli.
// Props: `cheque` (tek çek) VEYA `cheques` (dizi — toplu bankaya ver için)
function DepositToBankDialog({ cheque, cheques, onClose, onDone }) {
  const items = cheques && cheques.length > 0 ? cheques : (cheque ? [cheque] : []);
  const isBulk = items.length > 1;
  const first = items[0] || {};
  const [bankName, setBankName] = useState(
    !isBulk ? (first.deposit_bank_name || first.bank_name || "") : ""
  );
  const [depositDate, setDepositDate] = useState(new Date().toISOString().slice(0,10));
  const [expected, setExpected] = useState(!isBulk ? (first.due_date || "") : "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!bankName.trim()) { toast.error("Banka adı zorunlu"); return; }
    setBusy(true);
    try {
      if (isBulk) {
        const { data } = await api.post("/cheques/bulk-deposit-to-bank", {
          ids: items.map(c => c.id),
          bank_name: bankName.trim(),
          deposit_date: depositDate,
          expected_collection_date: expected || null,
          notes: notes.trim() || null,
        });
        const skipped = data.skipped?.length || 0;
        toast.success(`${data.deposited_count} çek bankaya verildi${skipped ? ` (${skipped} atlandı)` : ""}`);
      } else {
        await api.post(`/cheques/${first.id}/deposit-to-bank`, {
          bank_name: bankName.trim(),
          deposit_date: depositDate,
          expected_collection_date: expected || null,
          notes: notes.trim() || null,
        });
        toast.success("Çek bankaya verildi");
      }
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  // Toplam tutar (sadece çoklu görünümde ipucu olarak gösterilir)
  const totalTL = items.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="deposit-dialog">
        <DialogHeader>
          <DialogTitle>{isBulk ? `${items.length} Çeki Toplu Bankaya Ver` : "Çeki Bankaya Ver"}</DialogTitle>
          <DialogDescription>
            {isBulk
              ? <>Toplam: <span className="font-semibold">{fmtMoney(totalTL, "TRY")}</span> · <span className="font-mono">{items.length} çek</span></>
              : <><span className="font-mono">{first.cheque_no}</span> — {fmtMoney(first.amount, first.currency)} — Vade: {fmtDate(first.due_date)}</>
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs mb-1 block">Banka Adı *</Label>
            <Input value={bankName} onChange={e => setBankName(e.target.value)}
                   placeholder="Örn: Ziraat Bankası" data-testid="deposit-bank"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Bankaya Veriliş Tarihi</Label>
              <Input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)}
                     data-testid="deposit-date"/>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Tahsil Tarihi Tahmini</Label>
              <Input type="date" value={expected} onChange={e => setExpected(e.target.value)}
                     data-testid="deposit-expected"/>
              {isBulk && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Toplu işlemde tüm çekler aynı tahsil tarihi ile işaretlenir. Boş bırakırsanız bireysel vade tarihi kullanılır.
                </p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Notlar</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder="Opsiyonel — teslim eden yetkili, dekont no vb."
                      data-testid="deposit-notes"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={submit} disabled={busy} data-testid="deposit-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            {isBulk ? `${items.length} Çeki Bankaya Ver` : "Bankaya Ver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
